import React from 'react';
import PropTypes from 'prop-types';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  IconButton,
  Typography,
  CircularProgress
} from '@material-ui/core';

import eyeIcon from 'core/assets/svg/view.svg';
import editIcon from 'core/assets/svg/edit.svg';
import { useToast } from 'core/components/toast';
import { dateTimeFormatter } from 'core/helpers/formatters';

import useStyles from './offer-document-list.styles';

const OfferDocumentList = ({ documents, onDownload, onRename, canRename, loading }) => {
  const classes = useStyles();

  const toast = useToast();

  const handleDownload = async (path) => {
    try {
      await onDownload(path);
    } catch {
      toast.notify({
        type: 'error',
        message: 'Failed to download document'
      });
    }
  };

  const formatDate = (dateString) => {
    try {
      return dateTimeFormatter(new Date(dateString));
    } catch {
      return 'Invalid date';
    }
  };

  if (loading) {
    return (
      <div className={classes.loadingContainer}>
        <CircularProgress size={24} />
        <Typography variant="body2" className={classes.loadingText}>
          Loading documents...
        </Typography>
      </div>
    );
  }

  if (!documents || documents.length === 0) {
    return (
      <div className={classes.emptyState}>
        <Typography variant="body2" className={classes.emptyText}>
          No documents have been uploaded to this offer yet
        </Typography>
      </div>
    );
  }

  return (
    <Table className={classes.table}>
      <TableHead>
        <TableRow>
          <TableCell className={classes.headerCell}>FILE NAME</TableCell>
          <TableCell className={classes.headerCell}>UPLOADED</TableCell>
          <TableCell className={classes.headerCell}>ACTIONS</TableCell>
        </TableRow>
      </TableHead>
      <TableBody>
        {documents.map((document) => {
          // Only operator uploads (offer/ namespace) carry an editable label; generated
          // docs/-prefixed documents are rejected by the backend, so don't offer rename.
          const isRelabelable = document.id.startsWith('offer/');
          return (
            <TableRow key={document.id} className={classes.tableRow}>
              <TableCell className={classes.fileNameCell}>
                <div className={classes.fileNameContainer}>
                  <span className={classes.fileName}>{document.displayName || document.fileName}</span>
                  {document.displayName ? (
                    <Typography variant="caption" className={classes.originalName}>
                      {document.fileName}
                    </Typography>
                  ) : null}
                </div>
              </TableCell>
              <TableCell className={classes.cell}>
                <div className={classes.uploadInfo}>
                  <div>{formatDate(document.uploadedAt)}</div>
                </div>
              </TableCell>
              <TableCell className={classes.actionsCell}>
                <div className={classes.actions}>
                  {canRename && isRelabelable ? (
                    <IconButton
                      aria-label="rename"
                      onClick={() => onRename(document)}
                      className={classes.actionButton}
                      size="small"
                    >
                      <img src={editIcon} alt="rename" />
                    </IconButton>
                  ) : null}
                  <IconButton
                    aria-label="download"
                    onClick={() => handleDownload(document.id)}
                    className={classes.actionButton}
                    size="small"
                  >
                    <img src={eyeIcon} alt="open" />
                  </IconButton>
                </div>
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
};

OfferDocumentList.propTypes = {
  documents: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      fileName: PropTypes.string.isRequired,
      uploadedAt: PropTypes.string.isRequired,
      displayName: PropTypes.string
    })
  ),
  onDownload: PropTypes.func.isRequired,
  onRename: PropTypes.func,
  canRename: PropTypes.bool,
  loading: PropTypes.bool
};

OfferDocumentList.defaultProps = {
  documents: [],
  onRename: () => {},
  canRename: false,
  loading: false
};

export default OfferDocumentList;
